import { z } from 'zod'
import { sponsorChildRefSchema } from './sponsor.schema'
import { sponsorAppSponsorshipSchema } from './sponsor-adopt.schema'

// Contrato da API do App do Padrinho — fonte da verdade: este diretorio (PWA-002 Clarification #2).
// Spec PWA-001 — doacao, pagamento, visita, impacto e campanhas (telas 04, 11–13, 15, 16 do design do TR).
// RN-27: o app nunca manipula dados de cartao — o cartao e tokenizado pelo provedor de pagamento e o app
// so trafega o token opaco (paymentMethodToken) e dados de exibicao (bandeira, final, validade).

const httpsUrl = z.string().url().refine(u => u.startsWith('https://'), 'URL deve usar HTTPS')
const money = z.number().int().min(0).max(100_000_000) // centavos

// PWA-006: debito em conta e, com o cartao, a forma mais incentivada pelo ChildFund
export const sponsorPaymentKindSchema = z.enum(['card', 'debit', 'pix', 'boleto'])
export type SponsorPaymentKind = z.infer<typeof sponsorPaymentKindSchema>

export const sponsorSavedCardSchema = z.object({
    token: z.string().min(8).max(200),
    brand: z.string().min(1).max(20),
    last4: z.string().regex(/^\d{4}$/),
    holder: z.string().min(1).max(80),
    expiresAt: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
}).strict()
export type SponsorSavedCard = z.infer<typeof sponsorSavedCardSchema>

// ─── 11 · Doacoes e pagamentos ───────────────────────────────

export const sponsorPaymentRecordSchema = z.object({
    id: z.string().uuid(),
    description: z.string().min(1).max(120),
    date: z.string().date(),
    amountCents: money,
    kind: z.enum(['subscription', 'one_off']),
    status: z.enum(['paid', 'declined', 'recovered', 'pending']),
    method: z.object({ kind: sponsorPaymentKindSchema, last4: z.string().regex(/^\d{4}$/).nullable() }).strict(),
    // RN-24: pagamento recusado tenta de novo automaticamente em D+4
    retryAt: z.string().date().nullable(),
}).strict()
export type SponsorPaymentRecord = z.infer<typeof sponsorPaymentRecordSchema>

// Datas especiais (DFC — doacoes em datas comemorativas): contribuicao extra configurada no apadrinhamento,
// cobrada na data; `lastParticipation` = ultima vez que o padrinho participou (quando o CRM registra).
export const SPONSOR_SPECIAL_OCCASIONS = ['christmas', 'easter', 'childrens_day', 'birthday'] as const
export const sponsorSpecialOccasionSchema = z.enum(SPONSOR_SPECIAL_OCCASIONS)
export type SponsorSpecialOccasion = z.infer<typeof sponsorSpecialOccasionSchema>

export const sponsorSpecialDateSchema = z.object({
    occasion: sponsorSpecialOccasionSchema,
    amountCents: money.min(1),
    lastParticipation: z.string().date().nullable(),
}).strict()
export type SponsorSpecialDate = z.infer<typeof sponsorSpecialDateSchema>

export const sponsorDonationsSchema = z.object({
    subscription: z.object({
        amountCents: money,
        frequency: z.literal('monthly'),
        nextChargeAt: z.string().date(),
        // Forma da contribuicao (PWA-006): cartao (com `card`), debito em conta, ou null se o CRM nao informa
        method: sponsorPaymentKindSchema.nullable(),
        card: sponsorSavedCardSchema.nullable(),
        // PWA-024: ultimo vencimento registrado no CRM (null se nao ha)
        lastChargeAt: z.string().date().nullable(),
        // PWA-024: conta do debito automatico, so banco e final (nunca agencia nem numero inteiro)
        debitAccount: z.object({
            bank: z.string().min(1).max(60).nullable(),
            last4: z.string().regex(/^\d{4}$/).nullable(),
        }).strict().nullable(),
    }).strict().nullable(),
    // PWA-024: faturas em aberto no CRM (0 = em dia)
    openInvoices: z.number().int().min(0).max(999),
    alerts: z.array(z.object({
        kind: z.enum(['card_expiring', 'payment_declined']),
        date: z.string().date(),
        last4: z.string().regex(/^\d{4}$/).nullable(),
    }).strict()),
    payments: z.array(sponsorPaymentRecordSchema),
    // RN-25: comprovante anual de doacoes (PWA-006: doacao para apadrinhamento nao e dedutivel no IR; nao falar em IR)
    receiptYears: z.array(z.number().int().min(2000).max(2100)),
    // Uma entrada por data comemorativa com valor configurado (vazio = nao participa)
    specialDates: z.array(sponsorSpecialDateSchema).max(SPONSOR_SPECIAL_OCCASIONS.length),
    // Spec PWA-015 C#7: apadrinhamentos feitos pelo app (fora do Dynamics)
    appSponsorships: z.array(sponsorAppSponsorshipSchema),
}).strict()
export type SponsorDonations = z.infer<typeof sponsorDonationsSchema>

export const sponsorSubscriptionUpdateSchema = z.object({
    amountCents: money.min(1000),
}).strict()

export const sponsorCardUpdateSchema = z.object({
    paymentMethodToken: z.string().min(8).max(200),
}).strict()

export const sponsorReceiptSchema = z.object({ year: z.number().int(), url: httpsUrl, expiresAt: z.string().datetime() }).strict()

// ─── 12 · Produtos de doacao (RN-26: todo produto declara destino) ───────────────────────────────

export const sponsorDonationProductSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(['one_off', 'kit', 'sponsorship']),
    title: z.string().min(1).max(80),
    description: z.string().min(1).max(240),
    destination: z.string().min(1).max(120),
    amountCents: money.min(100),
    recurring: z.boolean(),
    imageUrl: z.string().url().nullable(),
    imageAlt: z.string().max(200).nullable(),
    highlight: z.boolean(),
}).strict()
export type SponsorDonationProduct = z.infer<typeof sponsorDonationProductSchema>

export const sponsorDonationCatalogSchema = z.object({
    products: z.array(sponsorDonationProductSchema),
    suggestedAmountsCents: z.array(money.min(100)).max(6),
    minFreeAmountCents: money.min(100),
    freeDestination: z.string().min(1).max(120),
}).strict()
export type SponsorDonationCatalog = z.infer<typeof sponsorDonationCatalogSchema>

// ─── 13 · Pagamento ───────────────────────────────

export const sponsorCheckoutSchema = z.object({
    productId: z.string().min(1).max(64).nullable(),
    // Valor livre (quando productId e null); centavos
    amountCents: money.min(100).nullable(),
    method: sponsorPaymentKindSchema,
    paymentMethodToken: z.string().min(8).max(200).nullable(),
}).strict().refine(v => (v.productId ? v.amountCents === null : v.amountCents !== null), 'Informe produto OU valor livre')
    .refine(v => v.method !== 'card' || !!v.paymentMethodToken, 'Cartao exige token do provedor')
export type SponsorCheckoutInput = z.infer<typeof sponsorCheckoutSchema>

export const sponsorCheckoutResultSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(['paid', 'pending', 'declined']),
    amountCents: money,
    description: z.string().min(1).max(120),
    pix: z.object({ copyPaste: z.string().min(10).max(600), qrCodeUrl: z.string().url(), expiresAt: z.string().datetime() }).strict().nullable(),
    boleto: z.object({ digitableLine: z.string().min(10).max(80), pdfUrl: httpsUrl, dueDate: z.string().date() }).strict().nullable(),
}).strict()
export type SponsorCheckoutResult = z.infer<typeof sponsorCheckoutResultSchema>

// ─── 15 · Solicitar visita (RN-30/31) ───────────────────────────────

export const sponsorVisitRequestSchema = z.object({
    childRef: sponsorChildRefSchema,
    preferredDate: z.string().date(),
    alternativeDate: z.string().date(),
    companions: z.string().trim().max(300),
    reason: z.string().trim().min(10).max(1000),
    acceptedRules: z.literal(true),
}).strict().refine(v => v.preferredDate !== v.alternativeDate, 'As datas precisam ser diferentes')
export type SponsorVisitRequestInput = z.infer<typeof sponsorVisitRequestSchema>

export const sponsorVisitRequestResponseSchema = z.object({
    id: z.string().uuid(),
    status: z.literal('requested'),
    // Resposta em ate 15 dias uteis, registrada na caixa de mensagens
    respondBy: z.string().date(),
}).strict()

// ─── 16 · Impacto (RN-32: todo numero traz fonte e data) ───────────────────────────────

export const sponsorImpactSchema = z.object({
    updatedAt: z.string().date(),
    sponsorshipMonths: z.number().int().min(0),
    totalDonatedCents: money,
    donatingSince: z.string().date().nullable(),
    served: z.object({
        ospName: z.string().min(1).max(80),
        series: z.array(z.object({ year: z.number().int(), children: z.number().int().min(0) }).strict()).min(1).max(10),
        source: z.string().min(1).max(160),
    }).strict().nullable(),
    allocation: z.array(z.object({ label: z.string().min(1).max(60), percent: z.number().min(0).max(100) }).strict()).max(8),
    allocationSource: z.string().max(160).nullable(),
    imageUrl: z.string().url().nullable(),
    imageAlt: z.string().max(200).nullable(),
    reportUrl: httpsUrl.nullable(),
}).strict()
export type SponsorImpact = z.infer<typeof sponsorImpactSchema>

// ─── 04 · Eventos acontecendo (PWA-028) ───────────────────────────────

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/** Campanha Live!Concerts do CRM que cruza hoje (D-01): nome, periodo e a imagem se houver — o CRM nao traz local nem o dia do show. */
export const sponsorEventSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    /** AAAA-MM-DD, dia de Brasilia */
    startDate: isoDay,
    endDate: isoDay,
    /** D-09: imagem da campanha, quando o CRM tem (caminho do app; no sandbox, endereco do arquivo de exemplo). */
    imageUrl: z.string().max(500).refine(v => v.startsWith('/api/v1/sponsor/events/') || /^https?:\/\//.test(v)).nullable(),
    /** D-10: onde e (ERP): o local do evento ou a igreja ligada, cidade e UF. Sem rua nem numero (nao existem). */
    place: z.object({
        name: z.string().min(1).max(160).nullable(),
        city: z.string().min(1).max(80).nullable(),
        state: z.string().regex(/^[A-Z]{2}$/).nullable(),
    }).strict().nullable(),
}).strict()
export type SponsorEvent = z.infer<typeof sponsorEventSchema>

export const sponsorEventsSchema = z.object({ events: z.array(sponsorEventSchema).max(200) }).strict()
export type SponsorEvents = z.infer<typeof sponsorEventsSchema>
